'use es6';

import { createSelector } from 'reselect';
import { createHasScopeSelector, getIsUngatedForImmerContentModel } from 'ContentEditorUI/redux/selectors/authSelectors';
import { getIsSaveInProgress, getIsSavePermanentlyDisabled } from 'ContentEditorUI/redux/selectors/saveDataSelectors';
import { getContentModel } from 'ContentEditorUI/redux/selectors/baseContentModelSelectors';
import { getModuleSaveData } from 'ContentEditorUI/redux/selectors/moduleSaveSelectors';
import { isTemporarySlug } from 'ContentEditorUI/utils/contentModelURLUtils';
import { basicSelectorWithStats, makeGatedSelector } from 'ContentEditorUI/redux/selectors/helpers';
import { getIsReadOnlyMode } from 'ContentEditorUI/redux/selectors/appStatusSelectors';
import { getIsUngatedForCMPPublishing } from './authSelectors';
export const getHasPermissionsToSave = createHasScopeSelector('blog-write');
export const getIsSavingEnabled = createSelector([getHasPermissionsToSave, getIsSaveInProgress, getIsSavePermanentlyDisabled], (hasPermissionsToSave, isSaveInProgress, isSavePermanentlyDisabled) => hasPermissionsToSave && !isSaveInProgress && !isSavePermanentlyDisabled);
let cachedTagIdsAsImmutable = null;
let cachedTagIdsAsArray = null;
const _getImmutableModelSaveData = createSelector([getContentModel, getIsUngatedForCMPPublishing], (contentModel, isUngatedForCMPPublishing) => {
  if (!cachedTagIdsAsImmutable || cachedTagIdsAsImmutable !== contentModel.get('tagIds')) {
    cachedTagIdsAsImmutable = contentModel.get('tagIds');
    cachedTagIdsAsArray = cachedTagIdsAsImmutable.toArray();
  }
  const result = {
    blog_author_id: contentModel.get('blogAuthorId'),
    campaign: contentModel.get('campaign'),
    campaign_name: contentModel.get('campaignName'),
    campaign_utm: contentModel.get('campaignUtm'),
    content_group_id: contentModel.get('contentGroupId'),
    enable_google_amp_output_override: contentModel.get('enableGoogleAmpOutputOverride'),
    featured_image: contentModel.get('featuredImage'),
    featured_image_alt_text: contentModel.get('featuredImageAltText'),
    html_title: contentModel.get('htmlTitle'),
    head_html: contentModel.get('headHtml'),
    is_crawlable_by_bots: contentModel.get('isCrawlableByBots'),
    link_rel_canonical_url: contentModel.get('linkRelCanonicalUrl'),
    meta_description: contentModel.get('metaDescription'),
    name: contentModel.get('name'),
    post_body: contentModel.get('postBody'),
    public_access_rules: contentModel.get('publicAccessRules'),
    public_access_rules_enabled: contentModel.get('publicAccessRulesEnabled'),
    slug: contentModel.get('slug'),
    state: contentModel.get('state'),
    text_to_audio_file_id: contentModel.get('textToAudioFileId'),
    topic_ids: cachedTagIdsAsArray,
    use_featured_image: contentModel.get('useFeaturedImage')
  };
  if (!isUngatedForCMPPublishing) {
    result.publish_date = contentModel.get('publishDate');
    result.publish_immediately = contentModel.get('publishImmediately');
  }
  return result;
});
const _getImmerModelSaveData = createSelector([getContentModel, getIsUngatedForCMPPublishing], (contentModel, isUngatedForCMPPublishing) => {
  const result = {
    blog_author_id: contentModel.blogAuthorId,
    campaign: contentModel.campaign,
    campaign_name: contentModel.campaignName,
    campaign_utm: contentModel.campaignUtm,
    content_group_id: contentModel.contentGroupId,
    enable_google_amp_output_override: contentModel.enableGoogleAmpOutputOverride,
    featured_image: contentModel.featuredImage,
    featured_image_alt_text: contentModel.featuredImageAltText,
    html_title: contentModel.htmlTitle,
    head_html: contentModel.headHtml,
    is_crawlable_by_bots: contentModel.isCrawlableByBots,
    link_rel_canonical_url: contentModel.linkRelCanonicalUrl,
    meta_description: contentModel.metaDescription,
    name: contentModel.name,
    post_body: contentModel.postBody,
    public_access_rules: contentModel.publicAccessRules,
    public_access_rules_enabled: contentModel.publicAccessRulesEnabled,
    slug: contentModel.slug,
    state: contentModel.state,
    text_to_audio_file_id: contentModel.textToAudioFileId,
    topic_ids: contentModel.tagIds,
    use_featured_image: contentModel.useFeaturedImage
  };
  if (!isUngatedForCMPPublishing) {
    result.publish_date = contentModel.publishDate;
    result.publish_immediately = contentModel.publishImmediately;
  }
  return result;
});
const _getModelSaveData = makeGatedSelector(getIsUngatedForImmerContentModel, _getImmutableModelSaveData, _getImmerModelSaveData);
export const getModelSaveData = basicSelectorWithStats((state, opts = {}) => {
  const modelSaveData = _getModelSaveData(state);
  const {
    slug
  } = modelSaveData;
  if (opts.isSaveBeforePublish && isTemporarySlug(slug)) {
    modelSaveData.slug = slug.substring(0, slug.indexOf('-temporary-slug-'));
  }
  return modelSaveData;
});
export const getSaveData = createSelector([getModelSaveData, getModuleSaveData], (modelSaveData, moduleSaveData) => Object.assign({}, modelSaveData, moduleSaveData));
export const getCanSave = createSelector([getHasPermissionsToSave, getIsSavePermanentlyDisabled, getIsReadOnlyMode], (hasPermissionsToSave, isSavePermanentlyDisabled, isReadOnly) => hasPermissionsToSave && !isSavePermanentlyDisabled && !isReadOnly);